/**
 * Route table and primary navigation.
 *
 * One definition, consumed by the router, the desktop header and the
 * mobile tab bar, so the three can never drift apart.
 *
 * Room URLs are `/room/:roomId`, deliberately NOT `/game/:roomId`, which
 * would collide with `/games/:gameId` (PRD §9).
 */

export const ROUTES = Object.freeze({
  ROOT: "/",
  WELCOME: "/welcome",
  HOME: "/home",
  GAMES: "/games",
  GAME: "/games/:gameId",
  ROOM: "/room/:roomId",
  LEARN: "/learn",
  /**
   * Clubs and players are their own section, not a corner of Learn.
   *
   * They started under `/learn/players` because that is where a beginner
   * goes to ask "who is that?". In practice it buried them: Learn is a
   * reading list, and a squad list sitting inside it made both harder to
   * scan. They are also the part people come back to during a game, which
   * is a different job from working through concepts.
   *
   * The old paths still resolve. They are redirected rather than dropped,
   * because they have been linked and bookmarked.
   */
  TEAMS: "/teams",
  PLAYER: "/teams/players/:playerId",
  LEGACY_PLAYERS: "/learn/players",
  LEGACY_PLAYER: "/learn/players/:playerId",
  CONCEPT: "/learn/:conceptId",
  PROFILE: "/profile",
  SETTINGS: "/settings",
  PRIVACY: "/privacy",
  TERMS: "/terms",
  STYLE_GUIDE: "/style-guide",
  NOT_FOUND: "/404",
});

export const toGame = (gameId) => `/games/${encodeURIComponent(gameId)}`;
export const toRoom = (roomId) => `/room/${encodeURIComponent(roomId)}`;
export const toConcept = (conceptId) => `/learn/${encodeURIComponent(conceptId)}`;
export const toPlayer = (playerId) =>
  `/teams/players/${encodeURIComponent(playerId)}`;

/**
 * The clubs list, optionally opened on one squad.
 *
 * The club is a query parameter rather than a path segment so the list
 * and the club's squad are the same page: going back from a player lands
 * on the squad you were reading, not at the top of the list.
 */
export const toPlayers = (teamId) =>
  teamId ? `/teams?team=${encodeURIComponent(teamId)}` : "/teams";

/** Same page, named for what it now is. */
export const toTeams = toPlayers;

export const toTeamGames = (teamId, year) => {
  const params = new URLSearchParams();
  params.set("team", String(teamId ?? "").trim().toUpperCase());
  params.set("tab", "games");
  if (year != null) params.set("season", String(year));
  return `/teams?${params.toString()}`;
};

/**
 * Home | Games | Teams | Learn | Profile.
 *
 * Teams sits next to Games rather than next to Learn, because it is used
 * the same way: something you open around a match to work out who you are
 * watching. Five items is the ceiling for the mobile tab bar, so anything
 * further has to earn its place by displacing one of these.
 */
export const PRIMARY_NAV = Object.freeze([
  {
    to: ROUTES.HOME,
    label: "Home",
    icon: "fa-solid fa-house",
    activeIcon: "fa-solid fa-house",
  },
  {
    to: ROUTES.GAMES,
    label: "Games",
    icon: "fa-solid fa-football",
    activeIcon: "fa-solid fa-football",
  },
  {
    to: ROUTES.TEAMS,
    label: "Teams",
    icon: "fa-solid fa-shield-halved",
    activeIcon: "fa-solid fa-shield-halved",
  },
  {
    to: ROUTES.LEARN,
    label: "Learn",
    icon: "fa-solid fa-graduation-cap",
    activeIcon: "fa-solid fa-graduation-cap",
  },
  {
    to: ROUTES.PROFILE,
    label: "Profile",
    icon: "fa-solid fa-user",
    activeIcon: "fa-solid fa-user",
  },
]);

/**
 * What a browsing visitor sees.
 *
 * Home is missing because it is a personal screen: progress, IQ, what to
 * learn next. There is nothing to put on it without an account, and an
 * empty Home is a worse first impression than no Home. Profile is missing
 * for the same reason, and because the banner above already offers the
 * one thing it would: making an account.
 */
export const BROWSE_NAV = Object.freeze(
  PRIMARY_NAV.filter((item) => item.to !== ROUTES.HOME && item.to !== ROUTES.PROFILE)
);
